import { useMemo } from 'react';

import { useAuth } from '@/app/providers/authContext';
// Submodule paths, not barrels: the shell is imported by every feature's routes, and the
// features' barrels reach back into `@/components/layout`.
import type { OrderSummary } from '@/features/orders/contracts';
import { useOrdersQuery } from '@/features/orders/queries';
import { useCurrentEstablishment } from '@/features/organization/queries';
import { useOpenDeliveriesQuery } from '@/features/tracking/queries';

import { profilesForNavigationItem } from '../navigation';

/**
 * What the storefront chrome says about the unit without opening a page (Figma D1, D3, M3): the
 * open order and when it closes, the boletos still to pay and the deliveries on their way. Read
 * from the first page of `/v1/orders` (newest first) and `/v1/deliveries` — the same requests the
 * order list and the tracking screen make, so the cache answers both.
 *
 * Boletos only for a profile the route guard lets into `/pagamentos` (the buyer): the operator's
 * menu, home and footer carry no boleto line (Figma 103:2, "o operador não vê Boletos").
 */
export interface StoreSummary {
  readonly buys: boolean;
  readonly paysBoletos: boolean;
  readonly establishmentName: string | undefined;
  readonly establishmentCount: number;
  /** The newest order still inside its editing hour, if any. */
  readonly openOrder: OrderSummary | undefined;
  readonly openOrderCount: number;
  /** Orders whose boleto is open or overdue, oldest due date first. */
  readonly boletosDue: readonly OrderSummary[];
  readonly deliveriesOnTheWay: number;
  /** The newest order that is not cancelled, for "Comprar de novo". */
  readonly lastOrder: OrderSummary | undefined;
}

export function useStoreSummary(): StoreSummary {
  const { profile } = useAuth();
  const buys = profile !== null && profilesForNavigationItem('cart').includes(profile);
  const paysBoletos = profile !== null && profilesForNavigationItem('payments').includes(profile);
  const { establishmentId, establishmentName, establishmentCount } = useCurrentEstablishment();
  const orders = useOrdersQuery({}, buys ? establishmentId : undefined);
  const deliveries = useOpenDeliveriesQuery();

  return useMemo(() => {
    const list = orders.data?.pages.flatMap((page) => page.items) ?? [];
    const open = list.filter((order) => order.status === 'placed' && order.editable);
    const due = paysBoletos
      ? list
          .filter((order) => order.boleto?.status === 'open' || order.boleto?.status === 'overdue')
          .sort((a, b) => (a.boleto?.due_date ?? '').localeCompare(b.boleto?.due_date ?? ''))
      : [];
    return {
      buys,
      paysBoletos,
      establishmentName,
      establishmentCount,
      openOrder: open[0],
      openOrderCount: open.length,
      boletosDue: due,
      deliveriesOnTheWay: buys ? deliveries.data.length : 0,
      lastOrder: list.find((order) => order.status !== 'cancelled'),
    };
  }, [buys, deliveries.data, establishmentCount, establishmentName, orders.data, paysBoletos]);
}
